import type { MinuteRange } from './drag';

/** The least a block must expose for the collision check — structural, so any board shape fits. */
export interface CollidableBlock {
  key: string;
  span: MinuteRange;
  needsRescheduling?: boolean;
}

/**
 * One line of "this is what is in the way", as the calendar warnings render it.
 *
 * Lives here rather than in a widget because three widgets show the same list —
 * `worker-calendar`'s `PausePopover`, `calendar-planning-overlay`'s `ConflictWarning`, and
 * `worker-task-plan` through its re-export. It used to be owned by `calendar-planning-overlay`,
 * which made `worker-calendar` import upward from a sibling widget.
 *
 * Distinct from the contract's `ScheduleConflictItem`: that is what the **server** returns with a
 * 409, this is what the **client** predicts before sending. The two are shown by different
 * components and are deliberately not unified.
 */
export interface ConflictItem {
  label: string;
  timeLabel: string;
}

/**
 * Which blocks in the target row the proposed range would overlap. Pure, no React.
 *
 * **Advisory only.** It sees one day's visible window, so it cannot know about entries before
 * 06:00 or after 22:00, entries on other days, or the completed-work blockers the server checks
 * separately. A red ghost means "the server will probably reject this"; the drop still goes through
 * and the server remains the authority.
 */
export function detectDragCollision<T extends CollidableBlock>(
  blocks: T[],
  proposed: MinuteRange,
  excludeKey: string,
): T[] {
  return blocks.filter((other) => {
    if (other.key === excludeKey) return false;
    // A flagged block does not reserve its time. `overlapWhere` in the API keeps
    // `needsRescheduling` entries out of the conflict check, so a drop on top of one is accepted —
    // warning about it would paint a red ghost over a slot that books fine, and re-planning the day
    // an absence wrecked is exactly when this check gets used.
    if (other.needsRescheduling) return false;
    // Ranges are half-open, so touching edges do not collide — 09:00–10:00 and 10:00–11:00 are fine.
    return other.span.startMin < proposed.endMin && proposed.startMin < other.span.endMin;
  });
}

/**
 * Whether the proposed range leaves the technician's shift. Pure, no React.
 *
 * **Advisory only**, exactly like `detectDragCollision`: an amber ghost means "the server will
 * probably ask for a confirmation", not "this is refused". The drop still goes through, and the
 * `Außerhalb der Arbeitszeit` dialog with its `allowOutsideWorkingHours` override remains the
 * authority.
 *
 * Mirrors the API rule verbatim — `startsAt < shiftStartsAt || endsAt > shiftEndsAt` in
 * `apps/api/src/modules/schedule/domain/working-hours.ts`. `proposed` is the **occupied block**,
 * travel and departure included, which is what the server's `plannedOccupancy` compares and what a
 * board span already is.
 *
 * A `null` shift is unconstrained: there is nothing to compare against, and the API treats a profile
 * without shift boundaries the same way. Pass `shiftWindow(shiftStart, shiftEnd)` — it is already
 * clamped to the drawn window, so a shift wider than 06:00–22:00 cannot flag a block the board can
 * draw in full.
 */
export function isOutsideShift(
  proposed: MinuteRange,
  shift: { startMin: number; endMin: number } | null,
): boolean {
  if (!shift) return false;
  return proposed.startMin < shift.startMin || proposed.endMin > shift.endMin;
}
